// Guard rails for POST /simulations/payments/{id}/actions.
//
// The simulator lets you advance a payment by hand, so it's easy to ask for a transition the payment
// isn't ready for — most commonly "Deliver" on a payment whose funds were never collected, which the
// API answers with a 500 rather than a typed error. These helpers turn that into an explanation.
//
// The catch is that the reported status is coarser than the lifecycle: a payment under review and one
// approved-but-not-collected are both `scheduled`, and collecting and in-delivery are both
// `in-progress`. So we only block what the status makes *certain* and leave everything else to the
// server, where a 5xx gets translated instead of pre-empted. Guessing more aggressively would block
// valid flows, which is worse than a clear error.

/** The lifecycle transitions `POST /simulations/payments/{id}/actions` accepts. */
export type SimulationAction = 'risk-approve' | 'risk-decline' | 'collect' | 'fail-collect' | 'deliver' | 'fail-deliver';

/** Payment statuses the API reports. A payment under review is reported as `scheduled`. */
export type PaymentStatus = 'scheduled' | 'in-progress' | 'completed' | 'failed' | 'canceled';

const ACTION_LABELS: Record<string, string> = {
  'risk-approve': 'Risk approve',
  'risk-decline': 'Risk decline',
  collect: 'Collect',
  'fail-collect': 'Fail collect',
  deliver: 'Deliver',
  'fail-deliver': 'Fail deliver',
};

/** Statuses from which nothing can advance any further. */
const TERMINAL: readonly string[] = ['completed', 'failed', 'canceled'];

/** Actions that require funds to have been collected first. */
const NEEDS_COLLECTED: readonly string[] = ['deliver', 'fail-deliver'];

/** Actions that act on the risk review, which only happens while the payment is still scheduled. */
const NEEDS_SCHEDULED: readonly string[] = ['risk-approve', 'risk-decline'];

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] || action;
}

/**
 * Why `action` can't apply to a payment in `status`, or null when it's worth sending.
 *
 * Returning null does not promise the call will succeed — the status can't reveal whether the review
 * or the collection already happened — so callers still need to handle a server-side rejection.
 */
export function blockedReason(action: string, status: string): string | null {
  const label = actionLabel(action);

  if (TERMINAL.includes(status)) {
    return `This payment is ${status}, which is the end of its lifecycle — "${label}" and the other simulation actions no longer apply. Create a new payment to simulate another run.`;
  }

  if (NEEDS_COLLECTED.includes(action) && status === 'scheduled') {
    return `"${label}" needs funds that have already been collected, but this payment is still scheduled — nothing has been debited from the funding source yet. Run "Collect" first (and "Risk approve" before it, if the payment is still under review), then try again.`;
  }

  if (NEEDS_SCHEDULED.includes(action) && status === 'in-progress') {
    return `"${label}" only applies while a payment is awaiting review, and this one is already in progress — its review has been settled. Use "Collect" or "Deliver" to advance it instead.`;
  }

  return null;
}

/**
 * Extra guidance to show alongside a 5xx from the actions endpoint. The endpoint reports an
 * out-of-order transition as a server error rather than a typed one, so the likeliest cause is that
 * the payment hasn't reached the stage this action follows.
 */
export function serverErrorHelp(action: string, status: string): string {
  const label = actionLabel(action);

  if (NEEDS_COLLECTED.includes(action)) {
    return `The most likely cause is that the payment's funds haven't been collected yet — "${status}" covers both collecting and in-delivery, so the status alone doesn't show it. Run "Collect", wait for it to be applied, then retry "${label}".`;
  }

  if (action === 'collect' || action === 'fail-collect') {
    return `The most likely cause is that the payment isn't ready to be collected — either its risk review is still open (run "Risk approve" first) or the funds were already collected. Check the request log below for the API's response.`;
  }

  return `The payment is most likely not at the stage this action follows. The order is risk approve → collect → deliver. Check the request log below for the API's response.`;
}
